(() => {
  'use strict';

  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  let parallaxBound = false;
  let parallaxFrame = 0;
  const live = (message) => {
    const region = $('[data-live-region]');
    if (region) region.textContent = message;
  };
  const escapeHTML = (value = '') => {
    const node = document.createElement('div');
    node.textContent = value;
    return node.innerHTML;
  };
  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'it-IT', {
        style: 'currency', currency: currency || document.body.dataset.currency || 'EUR'
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `€${((Number(cents) || 0) / 100).toFixed(2).replace('.', ',')}`;
    }
  };

  function trapOpen(panel, opener) {
    panel._opener = opener || document.activeElement;
    const focusable = $$('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', panel);
    if (focusable[0]) focusable[0].focus();
  }
  function restoreFocus(panel) {
    if (panel._opener && panel._opener.focus) panel._opener.focus();
  }
  function lock() { document.body.classList.add('is-locked'); }
  function unlockIfClear() {
    if (!$('.mobile-nav.is-open,.search-drawer.is-open,.cart-drawer.is-open')) document.body.classList.remove('is-locked');
  }

  function initAnnouncements(scope = document) {
    $$('[data-announcement-rotator]', scope).forEach((rotator) => {
      if (rotator.dataset.bound) return;
      rotator.dataset.bound = 'true';
      const items = $$('[data-announcement-item]', rotator);
      if (!items.length) return;
      let index = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
      let timer;
      const show = (nextIndex) => {
        index = (nextIndex + items.length) % items.length;
        items.forEach((item, itemIndex) => {
          const active = itemIndex === index;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
      };
      const stop = () => { if (timer) clearInterval(timer); timer = undefined; rotator._announcementTimer = undefined; };
      const start = () => {
        stop();
        if (items.length > 1 && document.visibilityState !== 'hidden') {
          timer = setInterval(() => show(index + 1), Math.max(3000, Number(rotator.dataset.interval) || 5000));
          rotator._announcementTimer = timer;
        }
      };
      rotator.addEventListener('mouseenter', stop);
      rotator.addEventListener('mouseleave', start);
      rotator.addEventListener('focusin', stop);
      rotator.addEventListener('focusout', start);
      rotator._showAnnouncement = show;
      show(index); start();
    });
  }

  function initHeader() {
    const header = $('[data-site-header]');
    if (!header) return;
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    update();
    addEventListener('scroll', update, { passive: true });

    const mobile = $('[data-mobile-nav]');
    const openMobile = (opener) => {
      if (!mobile) return;
      mobile.classList.add('is-open');
      mobile.setAttribute('aria-hidden', 'false');
      lock(); trapOpen($('.mobile-nav__panel', mobile), opener);
    };
    const closeMobile = () => {
      if (!mobile) return;
      mobile.classList.remove('is-open');
      mobile.setAttribute('aria-hidden', 'true');
      restoreFocus($('.mobile-nav__panel', mobile)); unlockIfClear();
    };
    $$('[data-mobile-open]').forEach((button) => button.addEventListener('click', () => openMobile(button)));
    $$('[data-mobile-close]', mobile || document).forEach((button) => button.addEventListener('click', closeMobile));

    const search = $('[data-search-drawer]');
    const openSearch = (opener) => {
      if (!search) return;
      if (mobile?.classList.contains('is-open')) closeMobile();
      search.classList.add('is-open'); search.setAttribute('aria-hidden', 'false'); lock();
      trapOpen($('.search-drawer__panel', search), opener);
      setTimeout(() => $('[type="search"]', search)?.focus(), 80);
    };
    const closeSearch = () => {
      if (!search) return;
      search.classList.remove('is-open'); search.setAttribute('aria-hidden', 'true');
      restoreFocus($('.search-drawer__panel', search)); unlockIfClear();
    };
    $$('[data-search-open]').forEach((button) => button.addEventListener('click', () => openSearch(button)));
    $$('[data-search-close]', search || document).forEach((button) => button.addEventListener('click', closeSearch));

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closeMobile(); closeSearch(); closeCart(); closeZoom();
    });
  }

  function initBeforeAfter(scope = document) {
    $$('[data-before-after]', scope).forEach((component) => {
      const range = $('[data-before-after-range]', component);
      if (!range || range.dataset.bound) return;
      range.dataset.bound = 'true';
      const update = () => {
        component.style.setProperty('--position', `${range.value}%`);
        range.setAttribute('aria-valuenow', range.value);
      };
      range.addEventListener('input', update); update();
    });
  }

  function initReveal(scope = document) {
    const items = $$('[data-reveal]', scope);
    if (!items.length || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-revealed')); return;
    }
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); observer.unobserve(entry.target); }
    }), { threshold: .12, rootMargin: '0px 0px -30px' });
    items.forEach((item) => observer.observe(item));
  }

  function initParallax() {
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => {
      parallaxFrame = 0;
      const items = $$('[data-parallax]');
      const shouldReduce = motionQuery.matches && document.body?.dataset.reduceMotion !== 'ignore';
      if (shouldReduce) {
        items.forEach((item) => {
          item.style.setProperty('--parallax-y', '0px');
          item.style.setProperty('--parallax-y-reverse', '0px');
        });
        return;
      }
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const mobileFactor = window.innerWidth < 750 ? .55 : 1;
      items.forEach((item) => {
        const rect = item.getBoundingClientRect();
        if (rect.bottom < -120 || rect.top > viewportHeight + 120) return;
        const masterStrength = Math.abs(Number(document.body?.dataset.parallaxMaster || 80)) / 80;
        const strength = Math.abs(Number(item.dataset.parallaxStrength || 80)) * masterStrength * mobileFactor;
        const direction = Number(item.dataset.parallaxDirection || 1) < 0 ? -1 : 1;
        const distance = rect.top + rect.height / 2 - viewportHeight / 2;
        const progress = distance / (viewportHeight + rect.height);
        const offset = Math.max(-strength, Math.min(strength, -progress * strength * 2)) * direction;
        item.style.setProperty('--parallax-y', `${offset.toFixed(2)}px`);
        item.style.setProperty('--parallax-y-reverse', `${(-offset * .65).toFixed(2)}px`);
      });
    };
    const queue = () => {
      if (!parallaxFrame) parallaxFrame = requestAnimationFrame(update);
    };
    if (!parallaxBound) {
      parallaxBound = true;
      addEventListener('scroll', queue, { passive: true });
      addEventListener('resize', queue, { passive: true });
      motionQuery.addEventListener?.('change', queue);
    }
    queue();
  }

  function initVideos(scope = document) {
    $$('[data-video-play]', scope).forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const frame = button.closest('[data-video-story]');
        const video = $('video', frame);
        if (!video) return;
        if (video.paused) { video.play(); button.setAttribute('aria-label', 'Metti in pausa'); }
        else { video.pause(); button.setAttribute('aria-label', 'Riproduci'); }
      });
    });
  }

  function initCarousels(scope = document) {
    $$('[data-carousel]', scope).forEach((track) => {
      const section = track.closest('.shopify-section') || track.parentElement;
      const previous = $('[data-carousel-prev]', section);
      const next = $('[data-carousel-next]', section);
      const move = (direction) => {
        const first = track.firstElementChild;
        const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 16);
        track.scrollBy({ left: direction * ((first?.getBoundingClientRect().width || track.clientWidth) + gap), behavior: 'smooth' });
      };
      if (previous && !previous.dataset.bound) { previous.dataset.bound = 'true'; previous.addEventListener('click', () => move(-1)); }
      if (next && !next.dataset.bound) { next.dataset.bound = 'true'; next.addEventListener('click', () => move(1)); }
    });
  }

  function initSlideshows(scope = document) {
    $$('[data-slideshow]', scope).forEach((slideshow) => {
      if (slideshow.dataset.bound) return;
      slideshow.dataset.bound = 'true';
      const slides = $$('[data-slide]', slideshow);
      const dots = $$('[data-slide-to]', slideshow);
      if (slides.length < 2) return;
      let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let timer;
      const show = (nextIndex) => {
        index = (nextIndex + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
        dots.forEach((dot, dotIndex) => {
          dot.classList.toggle('is-active', dotIndex === index);
          dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
        });
      };
      const stop = () => { if (timer) clearInterval(timer); timer = undefined; };
      const start = () => {
        stop();
        if (slideshow.dataset.autoplay === 'true' && document.visibilityState !== 'hidden') {
          timer = setInterval(() => show(index + 1), Math.max(3000, Number(slideshow.dataset.interval) || 6000));
        }
      };
      $('[data-slide-prev]', slideshow)?.addEventListener('click', () => { show(index - 1); start(); });
      $('[data-slide-next]', slideshow)?.addEventListener('click', () => { show(index + 1); start(); });
      dots.forEach((dot) => dot.addEventListener('click', () => { show(Number(dot.dataset.slideTo)); start(); }));
      slideshow.addEventListener('mouseenter', stop);
      slideshow.addEventListener('mouseleave', start);
      slideshow.addEventListener('focusin', stop);
      slideshow.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', () => document.visibilityState === 'hidden' ? stop() : start());
      slideshow._showSlide = show;
      show(index); start();
    });
  }

  function initCountdowns(scope = document) {
    $$('[data-countdown]', scope).forEach((countdown) => {
      if (countdown._countdownTimer) clearInterval(countdown._countdownTimer);
      const end = new Date(countdown.dataset.countdown).getTime();
      const fields = {
        days: $('[data-days]', countdown), hours: $('[data-hours]', countdown),
        minutes: $('[data-minutes]', countdown), seconds: $('[data-seconds]', countdown)
      };
      const render = () => {
        if (!Number.isFinite(end)) {
          Object.values(fields).forEach((field) => { if (field) field.textContent = '--'; });
          return;
        }
        const remaining = Math.max(0, end - Date.now());
        const totalSeconds = Math.floor(remaining / 1000);
        const values = {
          days: Math.floor(totalSeconds / 86400),
          hours: Math.floor(totalSeconds / 3600) % 24,
          minutes: Math.floor(totalSeconds / 60) % 60,
          seconds: totalSeconds % 60
        };
        Object.entries(values).forEach(([key, value]) => { if (fields[key]) fields[key].textContent = String(value).padStart(2, '0'); });
        if (remaining === 0 && countdown._countdownTimer) clearInterval(countdown._countdownTimer);
      };
      render();
      if (Number.isFinite(end) && end > Date.now()) countdown._countdownTimer = setInterval(render, 1000);
    });
  }

  function initHotspots(scope = document) {
    $$('[aria-expanded].hotspot__button', scope).forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const next = button.getAttribute('aria-expanded') !== 'true';
        $$('.hotspot__button[aria-expanded="true"]').forEach((openButton) => openButton.setAttribute('aria-expanded', 'false'));
        button.setAttribute('aria-expanded', next ? 'true' : 'false');
      });
    });
  }

  function initQuantity(scope = document) {
    $$('[data-quantity]', scope).forEach((wrapper) => {
      if (wrapper.dataset.bound) return;
      wrapper.dataset.bound = 'true';
      const input = $('input', wrapper);
      $$('[data-quantity-change]', wrapper).forEach((button) => button.addEventListener('click', () => {
        const next = Math.max(Number(input.min || 1), Number(input.value || 1) + Number(button.dataset.quantityChange));
        input.value = next; input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
    });
  }

  async function getCart() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Impossibile caricare il carrello.');
    return response.json();
  }
  function renderCart(cart) {
    const drawer = $('[data-cart-drawer]');
    if (!drawer) return;
    $$('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
    });
    const titleCount = $('[data-cart-title-count]', drawer);
    if (titleCount) titleCount.textContent = cart.item_count ? `(${cart.item_count})` : '';
    const body = $('[data-cart-items]', drawer);
    const footer = $('[data-cart-footer]', drawer);
    if (!cart.items.length) {
      body.innerHTML = '<div class="cart-drawer__empty"><div><p>Il carrello è vuoto.</p><a class="button button--secondary button--small" href="/collections/all">Scopri ALIM</a></div></div>';
      footer.hidden = true;
    } else {
      body.innerHTML = cart.items.map((item, index) => {
        const image = item.image ? `<a class="drawer-line__image" href="${escapeHTML(item.url)}"><img src="${escapeHTML(item.image)}&width=180" alt="${escapeHTML(item.product_title)}" width="180" height="180"></a>` : '<span class="drawer-line__image"></span>';
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p class="drawer-line__variant">${escapeHTML(item.variant_title)}</p>` : '';
        return `<div class="drawer-line" data-line-key="${escapeHTML(item.key)}">${image}<div><a class="drawer-line__title" href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>${variant}<div class="quantity" data-quantity><button type="button" data-drawer-quantity="${index + 1}" data-value="${Math.max(0,item.quantity - 1)}" aria-label="Riduci quantità">−</button><input value="${item.quantity}" readonly aria-label="Quantità"><button type="button" data-drawer-quantity="${index + 1}" data-value="${item.quantity + 1}" aria-label="Aumenta quantità">+</button></div></div><div class="drawer-line__end"><span>${formatMoney(item.final_line_price, cart.currency)}</span><button class="drawer-line__remove" type="button" data-drawer-quantity="${index + 1}" data-value="0">Rimuovi</button></div></div>`;
      }).join('');
      footer.hidden = false;
      $('[data-cart-total]', drawer).textContent = formatMoney(cart.total_price, cart.currency);
      $$('[data-drawer-quantity]', body).forEach((button) => button.addEventListener('click', () => changeCart(button.dataset.drawerQuantity, button.dataset.value)));
    }
    const threshold = Number($('[data-shipping-progress]', drawer)?.dataset.threshold || 10000);
    const missing = Math.max(0, threshold - cart.total_price);
    const message = $('[data-shipping-message]', drawer);
    const bar = $('[data-shipping-bar]', drawer);
    if (message) message.textContent = missing > 0 ? `Ti mancano ${formatMoney(missing, cart.currency)} per la spedizione gratuita` : 'Hai ottenuto la spedizione gratuita';
    if (bar) bar.style.width = `${Math.min(100, cart.total_price / threshold * 100)}%`;
  }
  async function refreshCart() { const cart = await getCart(); renderCart(cart); return cart; }
  async function changeCart(line, quantity) {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
      });
      if (!response.ok) throw new Error('Aggiornamento non riuscito.');
      renderCart(await response.json());
    } catch (error) { live(error.message); }
  }
  async function openCart(opener) {
    const drawer = $('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.add('is-open'); drawer.setAttribute('aria-hidden', 'false'); lock();
    trapOpen($('.cart-drawer__panel', drawer), opener);
    try { await refreshCart(); } catch (error) { live(error.message); }
  }
  function closeCart() {
    const drawer = $('[data-cart-drawer]');
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open'); drawer.setAttribute('aria-hidden', 'true');
    restoreFocus($('.cart-drawer__panel', drawer)); unlockIfClear();
  }
  function initCart() {
    $$('[data-cart-open]').forEach((button) => button.addEventListener('click', (event) => {
      if (document.body.dataset.cartMode === 'page') return;
      event.preventDefault(); openCart(button);
    }));
    $$('[data-cart-close]').forEach((button) => button.addEventListener('click', closeCart));
    $$('.js-product-form').forEach((form) => bindProductForm(form));
  }
  function bindProductForm(form) {
    if (form.dataset.bound) return;
    form.dataset.bound = 'true';
    form.addEventListener('submit', async (event) => {
      if (document.body.dataset.cartMode === 'page') return;
      event.preventDefault();
      const button = $('[type="submit"]', form);
      const original = button?.innerHTML;
      if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Impossibile aggiungere il prodotto.');
        live(`${result.product_title || 'Prodotto'} aggiunto al carrello.`); await openCart(button);
      } catch (error) {
        const errorNode = $('[data-product-error]', form);
        if (errorNode) { errorNode.textContent = error.message; errorNode.hidden = false; }
        live(error.message);
      } finally {
        if (button) { button.disabled = false; button.removeAttribute('aria-busy'); if (original) button.innerHTML = original; }
      }
    });
  }

  function initProduct(scope = document) {
    $$('[data-product-root]', scope).forEach((root) => {
      if (root.dataset.bound) return;
      root.dataset.bound = 'true';
      let variants = [];
      try { variants = JSON.parse($('[data-product-variants]', root)?.textContent || '[]'); } catch (_) {}
      const form = $('.js-product-form', root);
      const idInput = $('[name="id"]', form);
      const addButton = $('[type="submit"]', form);
      const sticky = $('[data-sticky-buy]');
      const updateVariant = () => {
        const selected = $$('[data-option-select]', root).map((select) => select.value);
        const variant = variants.find((item) => item.options.every((option, index) => option === selected[index]));
        if (!variant) { if (addButton) { addButton.disabled = true; addButton.textContent = 'Non disponibile'; } return; }
        idInput.value = variant.id;
        const price = formatMoney(variant.price, document.body.dataset.currency);
        $$('[data-product-price]', root).forEach((node) => node.textContent = price);
        if (sticky) {
          $('[name="id"]', sticky).value = variant.id;
          $('[data-sticky-price]', sticky).textContent = price;
          const stickyButton = $('[type="submit"]', sticky);
          stickyButton.disabled = !variant.available; stickyButton.textContent = variant.available ? 'Aggiungi' : 'Esaurito';
        }
        if (addButton) { addButton.disabled = !variant.available; addButton.textContent = variant.available ? addButton.dataset.availableLabel : 'Esaurito'; }
        if (history.replaceState) history.replaceState({}, '', `${location.pathname}?variant=${variant.id}`);
        const mediaButton = variant.featured_media ? $(`[data-media-id="${variant.featured_media.id}"]`, root) : null;
        if (mediaButton) mediaButton.click();
      };
      $$('[data-option-select]', root).forEach((select) => select.addEventListener('change', updateVariant));
      $$('[data-media-id]', root).forEach((button) => button.addEventListener('click', () => {
        $$('[data-media-id]', root).forEach((item) => item.classList.remove('is-active'));
        button.classList.add('is-active');
        $$('[data-product-media]', root).forEach((media) => media.classList.toggle('is-active', media.dataset.productMedia === button.dataset.mediaId));
      }));
      if (sticky && form && 'IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => sticky.classList.toggle('is-visible', !entry.isIntersecting), { threshold: .2 }).observe(form);
      }
    });
    $$('.js-product-form', scope).forEach((form) => bindProductForm(form));
  }

  function openZoom(button) {
    const dialog = $('[data-image-zoom]');
    const image = $('[data-zoom-image]', dialog || document);
    const source = button?.dataset.zoomSrc;
    if (!dialog || !image || !source) return;
    image.src = source; image.alt = button.dataset.zoomAlt || '';
    if (dialog.showModal) dialog.showModal(); else dialog.setAttribute('open', '');
    lock();
  }
  function closeZoom() {
    const dialog = $('[data-image-zoom]');
    if (!dialog?.open) return;
    if (dialog.close) dialog.close(); else dialog.removeAttribute('open');
    unlockIfClear();
  }
  function initZoom(scope = document) {
    $$('[data-zoom-open]', scope).forEach((button) => button.addEventListener('click', () => openZoom(button)));
    $$('[data-zoom-close]').forEach((button) => button.addEventListener('click', closeZoom));
    $('[data-image-zoom]')?.addEventListener('click', (event) => { if (event.target === event.currentTarget) closeZoom(); });
  }

  function init(scope = document) {
    initAnnouncements(scope); initBeforeAfter(scope); initReveal(scope); initParallax(); initVideos(scope); initCarousels(scope); initSlideshows(scope); initCountdowns(scope); initHotspots(scope); initQuantity(scope); initProduct(scope); initZoom(scope);
  }
  document.addEventListener('DOMContentLoaded', () => { initHeader(); initCart(); init(); });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:select', () => initParallax());
  document.addEventListener('shopify:block:select', (event) => {
    const announcement = event.target.closest?.('[data-announcement-item]');
    const rotator = announcement?.closest('[data-announcement-rotator]');
    if (announcement && rotator?._showAnnouncement) rotator._showAnnouncement(Number(announcement.dataset.announcementIndex));
    const slide = event.target.closest?.('[data-slide]');
    const slideshow = slide?.closest('[data-slideshow]');
    if (slide && slideshow?._showSlide) slideshow._showSlide(Number(slide.dataset.slideIndex));
  });
  document.addEventListener('shopify:section:unload', (event) => {
    $$('[data-announcement-rotator]', event.target).forEach((rotator) => clearInterval(rotator._announcementTimer));
    $$('[data-countdown]', event.target).forEach((countdown) => clearInterval(countdown._countdownTimer));
  });
  document.addEventListener('alim:content:loaded', (event) => init(event.detail?.root || document));
})();
